import { SONIC_GENRES, SONIC_ERAS, SONIC_TIMBRES, SONIC_VIBES, } from "./vocabulary.js";
// Default slot weights — vocal/guitar carry more of the perceived sonic
// identity (spec §5.1). This is the canonical source; the band API schema
// (Phase 4) imports it rather than re-declaring.
export const DEFAULT_SLOT_WEIGHTS = {
    vocal: 3,
    guitar: 3,
    bass: 1,
    drums: 1,
    keys: 2,
};
/**
 * Sum slot weights per term for a list axis, then return the up-to-3
 * highest-weighted terms. Sort is by summed weight desc with a stable
 * tie-break on vocabulary order, so the result is deterministic and
 * independent of slot order.
 */
function aggregateListAxis(vocabulary, pick, slots) {
    const totals = new Map();
    for (const slot of slots) {
        if (slot.weight <= 0)
            continue;
        for (const term of pick(slot.traits) ?? []) {
            totals.set(term, (totals.get(term) ?? 0) + slot.weight);
        }
    }
    if (totals.size === 0)
        return undefined;
    const ranked = [...totals.keys()].sort((a, b) => {
        const diff = (totals.get(b) ?? 0) - (totals.get(a) ?? 0);
        if (diff !== 0)
            return diff;
        return vocabulary.indexOf(a) - vocabulary.indexOf(b);
    });
    return ranked.slice(0, 3);
}
/** Sum slot weights per `era` value; the highest-summed value wins. */
function aggregateEra(slots) {
    const totals = new Map();
    for (const slot of slots) {
        if (slot.weight <= 0)
            continue;
        const era = slot.traits.era;
        if (era === undefined)
            continue;
        totals.set(era, (totals.get(era) ?? 0) + slot.weight);
    }
    if (totals.size === 0)
        return undefined;
    let winner;
    let bestWeight = -1;
    for (const era of SONIC_ERAS) {
        const weight = totals.get(era);
        if (weight !== undefined && weight > bestWeight) {
            bestWeight = weight;
            winner = era;
        }
    }
    return winner;
}
/** Weighted mean of the defined `energy` values, rounded, clamped 1..5. */
function aggregateEnergy(slots) {
    let weightedSum = 0;
    let totalWeight = 0;
    for (const slot of slots) {
        if (slot.weight <= 0)
            continue;
        const energy = slot.traits.energy;
        if (energy === undefined)
            continue;
        weightedSum += energy * slot.weight;
        totalWeight += slot.weight;
    }
    if (totalWeight === 0)
        return undefined;
    const mean = Math.round(weightedSum / totalWeight);
    return Math.min(5, Math.max(1, mean));
}
/**
 * Compose the sonic traits of a band's occupied slots into one aggregated
 * `SonicTraits`, weighted by slot weight (spec §4.2). Pure and deterministic:
 * empty `slots`, slots with `{}` traits, and 0-weight slots contribute
 * nothing; an all-empty band returns `{}`.
 */
export function aggregateBandTraits(slots) {
    const result = {};
    const genres = aggregateListAxis(SONIC_GENRES, (t) => t.genres, slots);
    if (genres)
        result.genres = genres;
    const era = aggregateEra(slots);
    if (era !== undefined)
        result.era = era;
    const timbre = aggregateListAxis(SONIC_TIMBRES, (t) => t.timbre, slots);
    if (timbre)
        result.timbre = timbre;
    const vibe = aggregateListAxis(SONIC_VIBES, (t) => t.vibe, slots);
    if (vibe)
        result.vibe = vibe;
    const energy = aggregateEnergy(slots);
    if (energy !== undefined)
        result.energy = energy;
    return result;
}
//# sourceMappingURL=aggregate.js.map